import { getChatSessionsByConversationId, postMessage } from "@Api";
import { useAuth } from "@Hooks";
import { tryCatch } from "@Utils";
import {
  ChannelEnum,
  IChatConfig,
  IChatSession,
  IChatSessionStatus,
  IMessage,
  IMessageDirection,
  ISharedFullConversation,
  MessageType,
  Undefinable,
} from "atlas-shared";
import { useEffect, useState, useCallback } from "react";
import { message as toastMessage } from "antd";
import ChatSessionManager from "@Utils/ChatSessionManager";

export const useChatSession = (conversationId: number, conversation: Undefinable<ISharedFullConversation>) => {
  const auth = useAuth();
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [loadedMessages, setLoadedMessages] = useState<boolean>(false);
  const [loadingMessages, setLoadingMessages] = useState<boolean>(false);
  const [sendingMessage, setSendingMessage] = useState<boolean>(false);
  const [sessionStatus, setSessionStatus] = useState<IChatSessionStatus | null>(null);
  const [userIsTyping, setUserIsTyping] = useState<boolean>(false);

  const connectChatSession = useCallback(
    (chatConfigId: number, sessionId: string) => {
      // Check if this session is already connected and active
      if (ChatSessionManager.isSessionConnectedAndActive(conversationId, sessionId)) {
        return;
      }

      // Check if this agent is already connected to this conversation
      if (ChatSessionManager.isAgentConnectedToConversation(conversationId, auth.user.id)) {
        return;
      }

      ChatSessionManager.connectChatSession(conversationId, chatConfigId, sessionId, auth.user.id);
    },
    [conversationId, auth.user.id]
  );

  const sendMessage = useCallback(
    async (message: string, chatConfig: IChatConfig, chatSession: IChatSession) => {
      setSendingMessage(true);

      const { data, error } = await tryCatch(
        postMessage({
          text: message,
          direction: IMessageDirection.OUTBOUND,
          chatConfig: { id: chatConfig.id },
          chatSession: { sessionId: chatSession.sessionId },
          user: { id: auth.user.id },
          type: MessageType.TEXT,
        })
      );

      setSendingMessage(false);
      if (error) toastMessage.error("Error sending message");
    },
    [auth.user.id]
  );

  const handleTypingStart = useCallback(() => {
    ChatSessionManager.handleTypingStart(conversationId);
  }, [conversationId]);

  const handleTypingStop = useCallback(() => {
    ChatSessionManager.handleTypingStop(conversationId);
  }, [conversationId]);

  // Subscribe to chat session updates
  useEffect(() => {
    const unsubscribe = ChatSessionManager.subscribe(conversationId, {
      onMessagesUpdate: (newMessages) => {
        setMessages(newMessages);
      },
      onSessionStatusUpdate: (status) => {
        setSessionStatus(status);
      },
      onLoadingUpdate: (loading) => {
        setLoadingMessages(loading);
      },
      onUserTypingUpdate: (isTyping) => {
        setUserIsTyping(isTyping);
      },
    });

    return unsubscribe;
  }, [conversationId]);

  // Load initial messages
  useEffect(() => {
    const loadMessages = async () => {
      if (!loadedMessages && conversation?.main_channel === ChannelEnum.Chat) {
        ChatSessionManager.setLoadingMessages(conversationId, true);

        const { data: chatSessions, error } = await tryCatch(
          getChatSessionsByConversationId(conversationId)
        );

        if (error) {
          console.error("Error loading chat sessions:", error);
          ChatSessionManager.setLoadingMessages(conversationId, false);
          return;
        }

        const allMessages = chatSessions
          ?.flatMap((chatSession) => chatSession.chatSessionMessages)
          .reverse();

        ChatSessionManager.setMessages(conversationId, allMessages);
        setLoadedMessages(true);
      }
    };

    loadMessages();
  }, [loadedMessages, conversation, conversationId]);

  // Reset state when conversation changes
  useEffect(() => {
    setMessages([]);
    setLoadedMessages(false);
    setLoadingMessages(false);
    setSessionStatus(null);
  }, [conversationId]);

  // Helper to check if current user is joined
  const isCurrentUserJoined = ChatSessionManager.isAgentJoined(conversationId, auth.user.id);

  const loading = loadingMessages;
  const socket = ChatSessionManager.getSocket(conversationId);

  return {
    messages,
    loading,
    sendingMessage,
    sendMessage,
    connectChatSession,
    sessionStatus,
    socket,
    handleTypingStart,
    handleTypingStop,
    isCurrentUserJoined,
    userIsTyping,
  };
};
